import React, { useState, useEffect } from 'react';
import LocationSearch from './LocationSearch';
import LifeTimeline from './LifeTimeline';
import PeriodInsights from './PeriodInsights';
import VedicChart from './VedicChart';
import { API_BASE } from '../config';

function ChartPage({ onChartGeneration, chartData, loading, error, onRequireSignup }) {
  const [birthData, setBirthData] = useState({
    name: '',
    relationship: '',
    date: '',
    time: '',
    latitude: '',
    longitude: '',
    timezone: 'UTC',
    locationLabel: '',
  });

  const selectedFramework = 'parasara';
  const [selectedFocusArea, setSelectedFocusArea] = useState('career');
  const [formCollapsed, setFormCollapsed] = useState(false);

  // Saved profiles (when signed in)
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('astrogem_token') : null;
  const isSignedIn = !!token;

  useEffect(() => {
    if (chartData) setFormCollapsed(true);
  }, [chartData]);

  // Load saved profiles for signed-in users
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/v3/user/profiles`, {
      headers: { Authorization: 'Bearer ' + token },
    })
      .then(r => r.json())
      .then(r => { if (r.success) setSavedProfiles(r.profiles || []); })
      .catch(() => {});
  }, [token]);

  const loadProfile = (id) => {
    setSelectedProfileId(id);
    if (!id) return;
    const p = savedProfiles.find(p => String(p.id) === String(id));
    if (!p) return;
    setBirthData({
      name: p.name || '',
      relationship: p.relationship || '',
      date: p.date || '',
      time: p.time || '',
      latitude: String(p.latitude || ''),
      longitude: String(p.longitude || ''),
      timezone: p.timezone || 'UTC',
      locationLabel: p.location_label || '',
    });
  };

  const handleLocationSelect = (locationData) => {
    setBirthData(prev => ({
      ...prev,
      latitude: locationData.latitude.toString(),
      longitude: locationData.longitude.toString(),
      timezone: locationData.timezone,
      locationLabel: locationData.label || locationData.name || prev.locationLabel,
    }));
    if (selectedProfileId) setSelectedProfileId('');
  };

  const handleField = (field) => (e) => {
    setBirthData(prev => ({ ...prev, [field]: e.target.value }));
    if (selectedProfileId) setSelectedProfileId('');
  };

  const deleteProfile = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this saved profile?')) return;
    const r = await fetch(`${API_BASE}/api/v3/user/profiles/${id}`, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + token },
    }).then(r => r.json()).catch(() => ({ success: false }));
    if (r.success) {
      setSavedProfiles(prev => prev.filter(p => p.id !== id));
      if (String(selectedProfileId) === String(id)) {
        setSelectedProfileId('');
      }
    }
  };

  const handleCalculate = () => {
    if (!birthData.date || !birthData.time || !birthData.latitude || !birthData.longitude) {
      alert('Please fill in location, date, and time');
      return;
    }
    if (isSignedIn && !birthData.name.trim()) {
      if (!window.confirm('No name entered — this chart will not be saved to your profiles. Continue?')) return;
    }
    onChartGeneration({
      ...birthData,
      framework: selectedFramework,
    });
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-3">
      {/* COLLAPSED FORM SUMMARY (when chart generated) */}
      {chartData && formCollapsed && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-2 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 text-xs text-white/70 flex-wrap">
            {birthData.name && (
              <>
                <span className="font-bold text-white">👤 {birthData.name}</span>
                {birthData.relationship && <span className="text-white/50 text-[10px] uppercase">({birthData.relationship})</span>}
                <span>•</span>
              </>
            )}
            <span className="font-semibold text-white">📍 {birthData.locationLabel || `${birthData.latitude && parseFloat(birthData.latitude).toFixed(2)}°, ${birthData.longitude && parseFloat(birthData.longitude).toFixed(2)}°`}</span>
            <span>•</span>
            <span>{birthData.date}</span>
            <span>•</span>
            <span>{birthData.time}</span>
            <span>•</span>
            <span>{birthData.timezone}</span>
          </div>
          <button
            onClick={() => setFormCollapsed(false)}
            className="text-xs text-purple-400 hover:text-purple-300 font-semibold"
          >
            ✏️ Edit
          </button>
        </div>
      )}

      {/* FORM SECTION - COMPACT TOP BAR */}
      {(!chartData || !formCollapsed) && (
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 border border-slate-700 rounded-lg p-5 space-y-4 w-full">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Your Life Chart</h1>
            <p className="text-white/60 text-xs">Enter your birth details to explore your life timeline</p>
          </div>
          <div className="text-right text-xs text-white/50">
            {selectedFramework === 'parasara' ? '120-year Cycle' : '144-year Cycle'}
          </div>
        </div>

        {/* Saved Profiles picker — only when signed in & has saved profiles */}
        {isSignedIn && savedProfiles.length > 0 && (
          <div className="bg-purple-500/10 border border-purple-500/30 rounded p-2.5 flex items-center gap-2">
            <label className="text-xs font-semibold text-purple-200 uppercase whitespace-nowrap">
              📋 Saved ({savedProfiles.length})
            </label>
            <select
              value={selectedProfileId}
              onChange={(e) => loadProfile(e.target.value)}
              className="flex-1 px-3 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm focus:outline-none focus:border-purple-400"
            >
              <option value="">— Pick a saved profile, or fill the form below for a new person —</option>
              {savedProfiles.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.relationship ? ` (${p.relationship})` : ''} — {p.date} {p.time}
                </option>
              ))}
            </select>
            {selectedProfileId && !String(selectedProfileId).startsWith('pending') && (
              <button
                type="button"
                onClick={(e) => deleteProfile(selectedProfileId, e)}
                className="text-xs text-red-300 hover:text-red-200 px-2 py-1"
                title="Delete this saved profile"
              >
                🗑
              </button>
            )}
          </div>
        )}

        {/* Name + Relationship row */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="sm:col-span-3">
            <label className="block text-xs font-medium text-white/90 mb-1">
              Name {isSignedIn && <span className="text-white/40">(saved for next time)</span>}
            </label>
            <input
              type="text"
              value={birthData.name}
              onChange={handleField('name')}
              placeholder="e.g. You, Spouse, Child, Client"
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-purple-400 placeholder-white/40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-white/90 mb-1">Relationship</label>
            <select
              value={birthData.relationship}
              onChange={handleField('relationship')}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-purple-400"
            >
              <option value="">— optional —</option>
              <option value="self">Self</option>
              <option value="spouse">Spouse / Partner</option>
              <option value="child">Child</option>
              <option value="parent">Parent</option>
              <option value="sibling">Sibling</option>
              <option value="friend">Friend</option>
              <option value="client">Client</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
          {/* Location Search */}
          <div className="lg:col-span-2">
            <LocationSearch onLocationSelect={handleLocationSelect} initialValue={birthData.locationLabel} />
          </div>

          {/* Date */}
          <div>
            <label className="block text-xs font-medium text-white/90 mb-1">Birth Date</label>
            <input
              type="date"
              value={birthData.date}
              onChange={handleField('date')}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-purple-400"
            />
          </div>

          {/* Time */}
          <div>
            <label className="block text-xs font-medium text-white/90 mb-1">Birth Time</label>
            <input
              type="time"
              value={birthData.time}
              onChange={handleField('time')}
              className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-purple-400"
            />
          </div>

          {/* Ayanamsha Info */}
          <div>
            <label className="block text-xs font-medium text-white/90 mb-1">Ayanamsha</label>
            <div className="w-full px-3 py-2 bg-slate-700/30 border border-slate-600 rounded text-white/70 text-sm">
              Lahiri (Standard)
            </div>
          </div>
        </div>

        {/* Bottom Action Row */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleCalculate}
            disabled={loading || !birthData.date || !birthData.time || !birthData.latitude || !birthData.longitude}
            className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold rounded transition"
          >
            {loading ? '⏳ Generating...' : '✨ Generate Chart'}
          </button>

          {/* Quick Stats */}
          {birthData.latitude && (
            <div className="text-xs text-white/60 space-y-0.5">
              <p>Lat: {parseFloat(birthData.latitude).toFixed(2)}° | Lon: {parseFloat(birthData.longitude).toFixed(2)}°</p>
              <p>Timezone: {birthData.timezone}</p>
            </div>
          )}

          {error && (
            <div className="text-xs text-red-400 ml-auto">
              ⚠️ {error}
            </div>
          )}
        </div>
      </div>
      )}

      {/* MAIN CONTENT SECTION - LIFE TIMELINE */}
      {chartData && (
        <div className="bg-gradient-to-b from-slate-800/80 to-slate-900/80 border border-slate-700 rounded-lg p-3">
          <LifeTimeline chartData={chartData} framework={selectedFramework} onRequireSignup={onRequireSignup} />
        </div>
      )}

      {/* Q&A SECTION - BELOW TIMELINE */}
      {chartData && (
        <div className="bg-gradient-to-b from-slate-900/80 to-slate-950/80 border border-slate-700 rounded-lg p-3">
          <div className="max-w-3xl mx-auto">
            <div className="mb-4 pb-4 border-b border-slate-700">
              <h3 className="text-lg font-bold text-white mb-1">Ask About Your Current Period</h3>
              <p className="text-xs text-white/60">Get personalized insights for your current life period</p>
            </div>

            {/* Focus Area Selector */}
            <div className="mb-4">
              <label className="text-xs font-semibold text-white/80 uppercase mb-2 block">Focus Area</label>
              <select
                value={selectedFocusArea}
                onChange={(e) => setSelectedFocusArea(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-purple-400"
              >
                <option value="career">💼 Career & Work</option>
                <option value="relationship">💕 Relationships</option>
                <option value="health">🏥 Health & Wellness</option>
                <option value="lifePurpose">✨ Life Purpose</option>
                <option value="other">❓ Other Questions</option>
              </select>
            </div>

            {/* Q&A Display */}
            <div className="space-y-3 mb-4">
              <PeriodInsights chartData={chartData} framework={selectedFramework} focusArea={selectedFocusArea} />
            </div>

            {/* Paywall Notice */}
            <div className="mt-4 pt-4 border-t border-slate-700 text-center">
              <p className="text-xs text-white/50">💎 Premium: Unlock past & future period insights</p>
            </div>
          </div>
        </div>
      )}

      {/* Chart Summary & Visualization - Below Timeline */}
      {chartData && (
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 border border-slate-700 rounded-lg p-3 space-y-3">
          <div>
            <h3 className="text-sm font-bold text-white mb-3">Chart Overview</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="bg-slate-700/30 rounded p-3 border border-slate-600 text-center">
                <p className="text-white/60 text-xs uppercase font-semibold">Ascendant</p>
                <p className="text-white font-bold text-lg">{chartData.ascendant?.sign}</p>
                <p className="text-white/70 text-xs">{chartData.ascendant?.degree?.toFixed(1)}°</p>
              </div>

              <div className="bg-slate-700/30 rounded p-3 border border-slate-600 text-center">
                <p className="text-white/60 text-xs uppercase font-semibold">System</p>
                <p className="text-white font-bold text-lg">Parasara + Jaimini</p>
                <p className="text-white/70 text-xs">{selectedFramework === 'parasara' ? 'Vimshottari' : 'Chara'}</p>
              </div>

              <div className="bg-slate-700/30 rounded p-3 border border-slate-600 text-center">
                <p className="text-white/60 text-xs uppercase font-semibold">Ayanamsha</p>
                <p className="text-white font-bold text-lg">{chartData.ayanamsha?.name || 'Lahiri'}</p>
                <p className="text-white/70 text-xs">{chartData.ayanamsha?.value?.toFixed(2)}°</p>
              </div>

              <div className="bg-slate-700/30 rounded p-3 border border-slate-600 text-center">
                <p className="text-white/60 text-xs uppercase font-semibold">Timezone</p>
                <p className="text-white font-bold text-lg">{birthData.timezone.split('/')[1] || 'UTC'}</p>
                <p className="text-white/70 text-xs">Birth location</p>
              </div>
            </div>
          </div>

          {/* Vedic Chart Visualization */}
          <div className="border-t border-slate-700 pt-6">
            <h3 className="text-sm font-bold text-white mb-4">Birth Chart Diagram</h3>
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <VedicChart chartData={chartData} />
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!chartData && !loading && (
        <div className="bg-gradient-to-b from-slate-800 to-slate-900 border border-slate-700 rounded-b-lg p-12 text-center">
          <p className="text-white/60">📊 Generate your chart to explore your life timeline</p>
        </div>
      )}
    </div>
  );
}

export default ChartPage;
